/**
 * SINGLE-SHARE SPECIAL — SINGLE SOURCE OF TRUTH (Aureus, Aug 2026)
 *
 * Plain ESM JS so BOTH the Vite frontend and the Node API (api/**) import the
 * exact same values without drift (same pattern as lib/birthdayPromo.js).
 *
 * PROMO: during the window, ANY regular single-share purchase awards:
 *   - buyer:  +1 bonus share PER paid single share  (buy 1 get 1 free)
 *   - upline: +0.5 bonus share PER paid single share to the DIRECT (level-1)
 *             active sponsor ONLY (no multi-level cascade).
 *
 * WINDOW (SAST / UTC+2):
 *   start = 00:00 SAST on 2026-08-04  == 2026-08-03T22:00:00Z
 *   end   = 00:30 SAST on 2026-08-14  == 2026-08-13T22:30:00Z (inclusive)
 *   Owner ruling 2026-08-14: extended past midnight for late submissions;
 *   after 00:30 SAST no more BOGO.
 *
 * Auto-expiry: after endDate, isSingleShareSpecialActive() returns false — the
 * first-card special description, the mini countdown, and the bonus logic all go
 * inert with no code change required.
 *
 * SAFETY: gift issuance rows created from this config MUST use
 * shares_purchased=0 and total_amount=0 so the aureus_share_purchases INSERT
 * triggers (team-volume credit guarded by total_amount<=0, pool ledger guarded
 * by total_amount>0.20) do not fire on commission-free gift rows — identical to
 * the audited birthday gift path.
 *
 * DONATION NOTICE (display-only, NOT financial logic): during 2026-08-04 JP's
 * company donates 20% of proceeds to the Cravenby Care Centre; for 2026-08-05
 * through 2026-08-13 it donates 2.5%. See donationNoticeForDate() below — this is
 * purely text shown under the package cards; no amount is computed or moved here.
 */

export const SINGLE_SHARE_SPECIAL_CONFIG = {
  enabled: true,
  startDate: '2026-08-03T22:00:00Z', // 00:00 SAST 2026-08-04
  endDate: '2026-08-13T22:30:00Z', // 00:30 SAST 2026-08-14 (inclusive; late-submit grace)
  // Per PAID single share:
  buyerBonusPerShare: 1, // buy 1 get 1 free
  uplineBonusPerShare: 0.5, // to the DIRECT level-1 sponsor only
  uplineDepth: 1, // level-1 only (no cascade)
  markers: {
    buyerGift: 'SINGLE_SPECIAL_2026_BUYER_GIFT',
    uplineGift: 'SINGLE_SPECIAL_2026_UPLINE_GIFT',
  },
  // Display-only donation schedule (Cravenby Care Centre). Dates are SAST civil
  // days; percentages are shown as text, never used to compute a transfer.
  donation: {
    charityName: 'Cravenby Care Centre',
    launchDay: '2026-08-04', // 20%
    launchPercent: 20,
    tailStart: '2026-08-05', // 2.5% from here ...
    tailEnd: '2026-08-13', // ... through here (inclusive) = 9 days
    tailPercent: 2.5,
  },
};

/**
 * Whether the single-share special is currently active (date-gated + enabled).
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isSingleShareSpecialActive(now = new Date()) {
  if (!SINGLE_SHARE_SPECIAL_CONFIG.enabled) return false;
  const t = now.getTime();
  return (
    t >= Date.parse(SINGLE_SHARE_SPECIAL_CONFIG.startDate) &&
    t <= Date.parse(SINGLE_SHARE_SPECIAL_CONFIG.endDate)
  );
}

/**
 * Compute the bonus-share breakdown for a number of PAID single shares.
 * All values scale linearly per paid share:
 *   buyerBonus  = buyerBonusPerShare  * paidShares  (gift to buyer)
 *   uplineBonus = uplineBonusPerShare * paidShares  (gift to level-1 sponsor)
 * Returns null if paidShares is not a positive whole number.
 *
 * @param {unknown} paidShares whole number of single shares actually paid for
 * @returns {{ paidShares: number, buyerBonus: number, uplineBonus: number }|null}
 */
export function computeSingleShareSpecialBreakdown(paidShares) {
  const n = Number(paidShares);
  if (!Number.isFinite(n)) return null;
  const whole = Math.floor(n);
  if (whole < 1) return null;
  return {
    paidShares: whole,
    buyerBonus: SINGLE_SHARE_SPECIAL_CONFIG.buyerBonusPerShare * whole,
    uplineBonus: SINGLE_SHARE_SPECIAL_CONFIG.uplineBonusPerShare * whole,
  };
}

/**
 * Return the display-only donation notice applicable on a given date, or null
 * when outside the donation schedule. Compares SAST civil days (UTC+2) so the
 * "4th" and the "5th–13th" boundaries land on the intended local calendar days.
 * DISPLAY ONLY — never used to compute or move money.
 *
 * @param {Date} [now]
 * @returns {{ charityName: string, percent: number, phase: 'launch'|'tail' }|null}
 */
export function donationNoticeForDate(now = new Date()) {
  const d = SINGLE_SHARE_SPECIAL_CONFIG.donation;
  // SAST civil date = UTC + 2h, formatted YYYY-MM-DD.
  const sast = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  const day = sast.toISOString().slice(0, 10);
  if (day === d.launchDay) {
    return { charityName: d.charityName, percent: d.launchPercent, phase: 'launch' };
  }
  if (day >= d.tailStart && day <= d.tailEnd) {
    return { charityName: d.charityName, percent: d.tailPercent, phase: 'tail' };
  }
  return null;
}
